import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, MapPin, User, Mail, Phone, Car, Plus, X, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { mockAddOns } from '../utils/mock';
import { getServices, getAvailability, createBooking, createCheckoutSession } from '../utils/api';

const Booking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [services, setServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Multi-car state: array of cars with their service and size
  const [cars, setCars] = useState([
    { id: 1, serviceId: location.state?.selectedService || '', vehicleSize: 'normal', carModel: '' }
  ]);
  
  const [formData, setFormData] = useState({
    date: null,
    time: '',
    customerName: '',
    email: '',
    phone: '',
    countryPrefix: '+351',
    address: '',
    notes: ''
  });

  // Fetch services on mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (error) {
        console.error('Error fetching services:', error);
        toast.error('Failed to load services');
      }
    };
    fetchServices();
  }, []);

  // Fetch availability when date is selected
  useEffect(() => {
    const fetchAvailability = async () => {
      if (formData.date) {
        try {
          const dateStr = formData.date.toISOString().split('T')[0];
          const slots = await getAvailability(dateStr);
          // Filter to only show until 6pm
          const filteredSlots = slots.filter(slot => {
            const hour = parseInt(slot.time.split(':')[0]);
            const isPM = slot.time.includes('PM');
            const hour24 = isPM && hour !== 12 ? hour + 12 : hour;
            return hour24 <= 18;
          });
          setTimeSlots(filteredSlots);
        } catch (error) {
          console.error('Error fetching availability:', error);
          toast.error('Failed to load available times');
        }
      }
    };
    fetchAvailability();
  }, [formData.date]);

  const getServiceById = (serviceId) => services.find(s => s.id === serviceId);

  const calculateSizeAdjustment = (serviceId, vehicleSize) => {
    const service = getServiceById(serviceId);
    if (!service) return 0;
    
    const isBasic = serviceId === 'basic';
    
    if (vehicleSize === 'big') {
      return isBasic ? 15 : 30;
    } else if (vehicleSize === 'large') {
      return isBasic ? 25 : 60;
    }
    return 0;
  };

  const calculateCarPrice = (car) => {
    const service = getServiceById(car.serviceId);
    if (!service) return 0;
    return service.price + calculateSizeAdjustment(car.serviceId, car.vehicleSize);
  };

  const calculateTotal = () => {
    // Sum all car prices
    let total = cars.reduce((sum, car) => sum + calculateCarPrice(car), 0);
    
    // Add add-ons (applied once, not per car)
    selectedAddOns.forEach(addOnId => {
      const addOn = mockAddOns.find(a => a.id === addOnId);
      if (addOn) total += addOn.price;
    });
    
    return total;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCarChange = (carId, field, value) => {
    setCars(prev => prev.map(car => 
      car.id === carId ? { ...car, [field]: value } : car
    ));
  };

  const addCar = () => {
    const newId = Math.max(...cars.map(c => c.id)) + 1;
    setCars(prev => [...prev, { id: newId, serviceId: '', vehicleSize: 'normal', carModel: '' }]);
  };

  const removeCar = (carId) => {
    if (cars.length > 1) {
      setCars(prev => prev.filter(car => car.id !== carId));
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    handleInputChange('date', date);
  };

  const toggleAddOn = (addOnId) => {
    setSelectedAddOns(prev => {
      if (prev.includes(addOnId)) {
        return prev.filter(id => id !== addOnId);
      } else {
        return [...prev, addOnId];
      }
    });
  };

  const handleNext = () => {
    if (step === 1) {
      // Validate all cars have a service selected
      const invalidCar = cars.find(car => !car.serviceId);
      if (invalidCar) {
        toast.error('Please select a service for all cars');
        return;
      }
    }
    if (step === 2) {
      // Validate all cars have size selected and car model
      const invalidCar = cars.find(car => !car.vehicleSize || !car.carModel);
      if (invalidCar) {
        toast.error('Please select vehicle size and enter car model for all cars');
        return;
      }
    }
    if (step === 3 && (!formData.date || !formData.time)) {
      toast.error('Please select date and time');
      return;
    }
    if (step === 4) {
      if (!formData.customerName || !formData.email || !formData.phone || !formData.address) {
        toast.error('Please fill in all required fields');
        return;
      }
    }
    setStep(step + 1);
  };

  const handleBooking = async () => {
    try {
      setLoading(true);
      const totalAmount = calculateTotal();
      
      // Prepare booking data with multiple cars
      const bookingPayload = {
        cars: cars.map(car => ({
          serviceId: car.serviceId,
          serviceName: getServiceById(car.serviceId)?.name,
          vehicleSize: car.vehicleSize,
          carModel: car.carModel,
          price: calculateCarPrice(car)
        })),
        addOns: selectedAddOns.map(id => {
          const addon = mockAddOns.find(a => a.id === id);
          return { id: addon.id, name: addon.name, price: addon.price };
        }),
        date: formData.date.toISOString().split('T')[0],
        time: formData.time,
        customerName: formData.customerName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        notes: formData.notes || '',
        totalAmount: totalAmount
      };
      
      // Create booking
      const bookingResponse = await createBooking(bookingPayload);
      toast.success('Booking created! Redirecting to payment...');
      
      // Create checkout session
      const hostUrl = window.location.origin;
      const checkoutResponse = await createCheckoutSession(bookingResponse.bookingId, hostUrl);
      
      // Redirect to Stripe checkout
      window.location.href = checkoutResponse.checkoutUrl;
      
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
      setLoading(false);
    }
  };

  const disabledDates = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Disable today and past dates
    if (date < tomorrow) return true;
    
    // Disable Saturdays (6) and Sundays (0)
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-20 relative">
      {/* Subtle Porsche background */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <img 
          src="https://customer-assets.emergentagent.com/job_alinsideout-detail/artifacts/b4qlq17q_IMG_2427.jpeg"
          alt="Premium car detailing"
          className="w-full h-full object-cover fixed"
        />
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-16 relative z-10">
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-4 text-zinc-100 text-center">Book Your Appointment</h1>
          <div className="flex items-center justify-center space-x-3">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-zinc-100 text-zinc-950' : 'bg-zinc-800 text-zinc-500'
                }`}>
                  {s}
                </div>
                {s < 5 && <div className={`w-16 h-0.5 ${step > s ? 'bg-zinc-100' : 'bg-zinc-800'}`}></div>}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-5 mt-3 text-xs text-zinc-500">
            <span className={`text-center ${step >= 1 ? 'text-zinc-300' : ''}`}>Cars & Services</span>
            <span className={`text-center ${step >= 2 ? 'text-zinc-300' : ''}`}>Size & Add-ons</span>
            <span className={`text-center ${step >= 3 ? 'text-zinc-300' : ''}`}>Date & Time</span>
            <span className={`text-center ${step >= 4 ? 'text-zinc-300' : ''}`}>Your Details</span>
            <span className={`text-center ${step >= 5 ? 'text-zinc-300' : ''}`}>Review & Pay</span>
          </div>
        </div>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100">
              {step === 1 && 'Add Cars & Select Services'}
              {step === 2 && 'Vehicle Details & Add-Ons'}
              {step === 3 && 'Choose Date & Time'}
              {step === 4 && 'Your Information'}
              {step === 5 && 'Review & Pay'}
            </CardTitle>
            <CardDescription className="text-zinc-400">
              {step === 1 && 'Add your cars and choose a service for each'}
              {step === 2 && 'Select vehicle size and optional add-ons'}
              {step === 3 && 'Pick your preferred date and time slot'}
              {step === 4 && 'Tell us about you and where to come'}
              {step === 5 && 'Review your booking and proceed to payment'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Cars & Service Selection */}
            {step === 1 && (
              <div className="space-y-6">
                {cars.map((car, index) => (
                  <div key={car.id} className="border border-zinc-800 rounded-lg p-4 bg-zinc-950">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
                        <Car className="w-5 h-5" />
                        Car {index + 1}
                      </h3>
                      {cars.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCar(car.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`remove-car-${index + 1}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      {services.map((service) => (
                        <div
                          key={service.id}
                          onClick={() => handleCarChange(car.id, 'serviceId', service.id)}
                          data-testid={`car-${index + 1}-service-${service.id}`}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            car.serviceId === service.id
                              ? 'border-zinc-100 bg-zinc-800'
                              : 'border-zinc-800 hover:border-zinc-700 bg-zinc-900'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="font-semibold text-zinc-100">{service.name}</h4>
                              <p className="text-zinc-400 text-sm">{service.description}</p>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-zinc-100">€{service.price}</div>
                              <div className="text-xs text-zinc-500">{service.duration}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                <Button
                  onClick={addCar}
                  variant="outline"
                  className="w-full border-dashed border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500 bg-transparent"
                  data-testid="add-another-car-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Car
                </Button>
              </div>
            )}

            {/* Step 2: Vehicle Size & Add-Ons */}
            {step === 2 && (
              <div className="space-y-6">
                {/* Vehicle Sizes for each car */}
                {cars.map((car, index) => {
                  const service = getServiceById(car.serviceId);
                  const isBasic = car.serviceId === 'basic';
                  
                  return (
                    <div key={car.id} className="border border-zinc-800 rounded-lg p-4 bg-zinc-950">
                      <h3 className="text-lg font-semibold text-zinc-100 mb-4 flex items-center gap-2">
                        <Car className="w-5 h-5" />
                        Car {index + 1}: {service?.name}
                      </h3>
                      
                      {/* Car Model Input */}
                      <div className="mb-4">
                        <Label className="text-zinc-300 mb-2 block">Car Make & Model *</Label>
                        <Input
                          value={car.carModel}
                          onChange={(e) => handleCarChange(car.id, 'carModel', e.target.value)}
                          className="bg-zinc-900 border-zinc-800 text-zinc-100"
                          placeholder="e.g., Tesla Model 3, BMW X5"
                          data-testid={`car-${index + 1}-model-input`}
                        />
                      </div>
                      
                      {/* Vehicle Size Selection */}
                      <Label className="text-zinc-300 mb-3 block">Select Vehicle Size *</Label>
                      <div className="grid grid-cols-3 gap-3">
                        <div
                          onClick={() => handleCarChange(car.id, 'vehicleSize', 'normal')}
                          data-testid={`car-${index + 1}-size-normal`}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                            car.vehicleSize === 'normal'
                              ? 'border-zinc-100 bg-zinc-800'
                              : 'border-zinc-800 hover:border-zinc-700 bg-zinc-900'
                          }`}
                        >
                          <Car className="w-10 h-10 mx-auto mb-2 text-zinc-400" />
                          <div className="font-semibold text-zinc-100 text-sm">Small / Normal</div>
                          <div className="text-xs text-zinc-500 mt-1">Standard pricing</div>
                        </div>
                        <div
                          onClick={() => handleCarChange(car.id, 'vehicleSize', 'big')}
                          data-testid={`car-${index + 1}-size-big`}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                            car.vehicleSize === 'big'
                              ? 'border-zinc-100 bg-zinc-800'
                              : 'border-zinc-800 hover:border-zinc-700 bg-zinc-900'
                          }`}
                        >
                          <Car className="w-12 h-12 mx-auto mb-2 text-zinc-400" />
                          <div className="font-semibold text-zinc-100 text-sm">Big (SUV)</div>
                          <div className="text-xs text-zinc-500 mt-1">+€{isBasic ? '15' : '30'}</div>
                        </div>
                        <div
                          onClick={() => handleCarChange(car.id, 'vehicleSize', 'large')}
                          data-testid={`car-${index + 1}-size-large`}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                            car.vehicleSize === 'large'
                              ? 'border-zinc-100 bg-zinc-800'
                              : 'border-zinc-800 hover:border-zinc-700 bg-zinc-900'
                          }`}
                        >
                          <Car className="w-14 h-14 mx-auto mb-2 text-zinc-400" />
                          <div className="font-semibold text-zinc-100 text-sm">Large (Van)</div>
                          <div className="text-xs text-zinc-500 mt-1">+€{isBasic ? '25' : '60'}</div>
                        </div>
                      </div>
                      
                      {/* Car price summary */}
                      <div className="mt-4 flex justify-between items-center text-sm bg-zinc-900 p-3 rounded-lg">
                        <span className="text-zinc-400">This car total:</span>
                        <span className="text-zinc-100 font-semibold">€{calculateCarPrice(car)}</span>
                      </div>
                    </div>
                  );
                })}

                {/* Add-Ons */}
                <div className="border border-zinc-800 rounded-lg p-4 bg-zinc-950">
                  <h3 className="text-xl font-semibold text-zinc-100 mb-2">Optional Add-Ons</h3>
                  <p className="text-sm text-zinc-400 mb-4">These add-ons apply to your entire booking</p>
                  
                  <div className="space-y-3">
                    {mockAddOns.map((addOn) => (
                      <div
                        key={addOn.id}
                        onClick={() => toggleAddOn(addOn.id)}
                        data-testid={`addon-${addOn.id}`}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedAddOns.includes(addOn.id)
                            ? 'border-zinc-100 bg-zinc-800'
                            : 'border-zinc-800 hover:border-zinc-700 bg-zinc-900'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              selectedAddOns.includes(addOn.id)
                                ? 'border-zinc-100 bg-zinc-100'
                                : 'border-zinc-700'
                            }`}>
                              {selectedAddOns.includes(addOn.id) && (
                                <svg className="w-4 h-4 text-zinc-950" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <p className="font-medium text-zinc-100">{addOn.name}</p>
                          </div>
                          <div className="text-lg font-semibold text-zinc-100">+€{addOn.price}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Date & Time Selection */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <Label className="text-zinc-300 mb-3 block">Select Date</Label>
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={disabledDates}
                      className="rounded-md border border-zinc-800 bg-zinc-950"
                      classNames={{
                        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                        month: "space-y-4",
                        caption: "flex justify-center pt-1 relative items-center text-zinc-100",
                        caption_label: "text-sm font-medium",
                        nav: "space-x-1 flex items-center",
                        nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-zinc-400",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse space-y-1",
                        head_row: "flex",
                        head_cell: "text-zinc-500 rounded-md w-9 font-normal text-[0.8rem]",
                        row: "flex w-full mt-2",
                        cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-zinc-800 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                        day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-zinc-800 rounded-md text-zinc-300",
                        day_selected: "bg-zinc-100 text-zinc-950 hover:bg-zinc-200 hover:text-zinc-950 focus:bg-zinc-100 focus:text-zinc-950",
                        day_today: "bg-zinc-800 text-zinc-100",
                        day_outside: "text-zinc-600 opacity-50",
                        day_disabled: "text-zinc-600 opacity-50",
                        day_hidden: "invisible",
                      }}
                    />
                  </div>
                </div>
                {formData.date && (
                  <div>
                    <Label className="text-zinc-300 mb-3 block">Select Time</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {timeSlots.map((slot) => (
                        <Button
                          key={slot.time}
                          variant={formData.time === slot.time ? 'default' : 'outline'}
                          onClick={() => handleInputChange('time', slot.time)}
                          disabled={!slot.available}
                          data-testid={`time-slot-${slot.time.replace(/[:\s]/g, '-')}`}
                          className={`${
                            formData.time === slot.time
                              ? 'bg-zinc-100 text-zinc-950 hover:bg-zinc-200'
                              : 'bg-zinc-950 text-zinc-300 border-zinc-800 hover:bg-zinc-800'
                          } ${!slot.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {slot.time}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Customer Information */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customerName" className="text-zinc-300 mb-2 block">
                      <User className="w-4 h-4 inline mr-2" />
                      Full Name *
                    </Label>
                    <Input
                      id="customerName"
                      value={formData.customerName}
                      onChange={(e) => handleInputChange('customerName', e.target.value)}
                      className="bg-zinc-950 border-zinc-800 text-zinc-100"
                      placeholder="John Doe"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-zinc-300 mb-2 block">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="bg-zinc-950 border-zinc-800 text-zinc-100"
                      placeholder="john@example.com"
                      data-testid="customer-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone" className="text-zinc-300 mb-2 block">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100"
                    placeholder="+351 912 345 678"
                    data-testid="customer-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="address" className="text-zinc-300 mb-2 block">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Service Address *
                  </Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100"
                    placeholder="Your address where we will come"
                    data-testid="customer-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="notes" className="text-zinc-300 mb-2 block">
                    Additional Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100 min-h-24"
                    placeholder="Any special requests or concerns..."
                    data-testid="customer-notes-input"
                  />
                </div>
              </div>
            )}

            {/* Step 5: Review & Payment */}
            {step === 5 && (
              <div className="space-y-6">
                <div className="bg-zinc-950 p-6 rounded-lg border border-zinc-800">
                  <h3 className="text-lg font-semibold text-zinc-100 mb-4">Booking Summary</h3>
                  <div className="space-y-4 text-sm">
                    {/* Cars Summary */}
                    {cars.map((car, index) => {
                      const service = getServiceById(car.serviceId);
                      return (
                        <div key={car.id} className="border-b border-zinc-800 pb-3">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-zinc-300 font-medium">Car {index + 1}: {car.carModel}</span>
                            <span className="text-zinc-100 font-semibold">€{calculateCarPrice(car)}</span>
                          </div>
                          <div className="text-zinc-500 text-xs">
                            {service?.name} • {car.vehicleSize === 'normal' ? 'Small/Normal' : car.vehicleSize === 'big' ? 'Big (SUV)' : 'Large (Van)'}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Add-ons Summary */}
                    {selectedAddOns.length > 0 && (
                      <div className="border-b border-zinc-800 pb-3">
                        <div className="text-zinc-400 mb-2">Add-ons:</div>
                        {selectedAddOns.map(addOnId => {
                          const addOn = mockAddOns.find(a => a.id === addOnId);
                          return (
                            <div key={addOnId} className="flex justify-between text-xs ml-2 mb-1">
                              <span className="text-zinc-500">• {addOn?.name}</span>
                              <span className="text-zinc-400">+€{addOn?.price}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Date & Time */}
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Date:</span>
                      <span className="text-zinc-100">{formData.date?.toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Time:</span>
                      <span className="text-zinc-100">{formData.time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Address:</span>
                      <span className="text-zinc-100 text-right max-w-[200px]">{formData.address}</span>
                    </div>
                    
                    {/* Total */}
                    <div className="border-t border-zinc-800 pt-3 mt-3">
                      <div className="flex justify-between text-lg">
                        <span className="text-zinc-300 font-semibold">Total ({cars.length} car{cars.length > 1 ? 's' : ''}):</span>
                        <span className="text-zinc-100 font-bold">€{calculateTotal()}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-zinc-950 p-4 rounded-lg border border-zinc-800">
                  <p className="text-sm text-zinc-400">
                    By proceeding, you agree to our terms of service. Payment is required to confirm your booking. You will receive a confirmation email and Stripe receipt after payment.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {step > 1 && (
                <Button
                  onClick={() => setStep(step - 1)}
                  variant="outline"
                  className="bg-zinc-950 text-zinc-300 border-zinc-800 hover:bg-zinc-800"
                  data-testid="booking-back-btn"
                >
                  Back
                </Button>
              )}
              {step < 5 ? (
                <Button
                  onClick={handleNext}
                  className="bg-zinc-100 text-zinc-950 hover:bg-zinc-200 ml-auto"
                  data-testid="booking-next-btn"
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleBooking}
                  disabled={loading}
                  className="bg-zinc-100 text-zinc-950 hover:bg-zinc-200 ml-auto"
                  data-testid="booking-pay-btn"
                >
                  {loading ? 'Processing...' : `Proceed to Payment - €${calculateTotal()}`}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Booking;
